/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * THE LIVE STATUS BACKSTOP (A29, 14/10/2026). The officer dashboard learned of
 * a change made elsewhere (the second signatory's decision, a client submission,
 * the engine finishing) only through a Supabase Realtime subscription on
 * onboarding_sessions, and no table is published to Realtime on staging or
 * production (checked 14/10/2026), so it has never fired. So the
 * open case and the pipeline row stayed on the old status until a page reload
 * (measured: still "second sign off" 120 seconds after the approval). The
 * backstop reads the safe status fields of the firm's cases on a timer while the
 * tab is visible, and at once when the officer comes back to it, and overlays
 * them. It never reads or replaces the decision surface (report, tier, items):
 * a change there still arrives through the update banner.
 */

export const LIVE_STATUS_POLL_MS = 30 * 1000;

// The dashboard's SAFE_RESYNC_FIELDS (the fields an autonomous refresh may
// overlay on the open case), plus the id.
export const LIVE_STATUS_FIELDS = Object.freeze(['status', 'assigned_to', 'assigned_to_email', 'assigned_at',
  'payment_status', 'payment_confirmed_by', 'payment_confirmed_at', 'fee_amount', 'fee_currency',
  'fee_terms_accepted_at', 'has_updates', 'last_update_type', 'last_update_at',
  'next_review_date', 'last_review_date', 'last_review_by', 'monitoring_tier', 'analysis_progress']);
// The pipeline list also shows the tier and the client classification, and
// sorts by the update time. These overlay the LIST only: on the open case they
// are the decision surface, which changes only through the update banner.
export const LIVE_LIST_ONLY_FIELDS = Object.freeze(['risk_tier', 'client_classification', 'updated_at']);
export const LIVE_LIST_FIELDS = Object.freeze([...LIVE_STATUS_FIELDS, ...LIVE_LIST_ONLY_FIELDS]);
export const LIVE_STATUS_SELECT = ['id', ...LIVE_LIST_FIELDS].join(',');

/** The safe fields of a fresh row, for the open case (never the list only fields). */
export function safeOpenCaseFields(fresh) {
  const out = { id: fresh && fresh.id };
  for (const k of LIVE_STATUS_FIELDS) if (fresh && k in fresh) out[k] = fresh[k];
  return out;
}

const same = (a, b) => (a === b) || JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);

/**
 * Overlay fresh safe fields on the list. Returns { rows, changed, unknownIds }:
 * rows is the same array when nothing changed (so React does not re-render),
 * unknownIds are cases the list does not hold yet (a new invitation made
 * elsewhere), for which the caller reads the list in full.
 */
export function mergeLiveStatus(rows, fresh, fields = LIVE_STATUS_FIELDS) {
  const byId = new Map((Array.isArray(fresh) ? fresh : []).filter(r => r && r.id).map(r => [r.id, r]));
  const held = new Set();
  let changed = false;
  const out = (Array.isArray(rows) ? rows : []).map(row => {
    if (!row || !row.id) return row;
    held.add(row.id);
    const f = byId.get(row.id);
    if (!f) return row;
    let patch = null;
    for (const k of fields) if (k in f && !same(row[k], f[k])) { (patch = patch || {})[k] = f[k]; }
    if (!patch) return row;
    changed = true;
    return { ...row, ...patch };
  });
  const unknownIds = [...byId.keys()].filter(id => !held.has(id));
  return { rows: changed ? out : rows, changed, unknownIds };
}
